"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";

export type BrowserNotificationPermission =
  | "default"
  | "granted"
  | "denied"
  | "unsupported";

export interface BrowserNotificationPermissionState {
  permission: BrowserNotificationPermission;
  // False until the first client-side read of the native permission. Any
  // surface that nudges on "default" has to wait for this, or every user —
  // including ones who already granted — sees the nudge for a frame.
  isInitialized: boolean;
}

// Stable reference so useSyncExternalStore doesn't see a new snapshot on
// every read while the store is still cold.
const UNINITIALIZED_STATE: BrowserNotificationPermissionState = {
  permission: "default",
  isInitialized: false,
};

// Singletons so every consumer reads the same permission state. Permission
// changes are global to the document, so duplicating state per-hook would
// produce nothing but desync (a banner in the bell panel could disagree with
// the settings page).
let sharedState: BrowserNotificationPermissionState = UNINITIALIZED_STATE;
let sharedInitialized = false;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((cb) => cb());
}

function readNativePermission(): BrowserNotificationPermission {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return window.Notification.permission as BrowserNotificationPermission;
}

function setSharedPermission(next: BrowserNotificationPermission) {
  if (sharedState.isInitialized && sharedState.permission === next) return;
  sharedState = { permission: next, isInitialized: true };
  notifyListeners();
}

function initShared() {
  if (sharedInitialized || typeof window === "undefined") return;
  sharedInitialized = true;
  // Go through setSharedPermission so any subscribers that mounted before
  // the first init() (and got the "default" SSR/initial snapshot) are pushed
  // the real value instead of being stuck until another event fires.
  setSharedPermission(readNativePermission());
  if (sharedState.permission === "unsupported") return;

  // The Permissions API supports a change event; the Notification API itself
  // does not. Fall back to polling cheaply on focus when Permissions isn't
  // available so a permission change in the OS/browser settings catches up
  // within the current session.
  if (
    "permissions" in navigator &&
    typeof navigator.permissions.query === "function"
  ) {
    navigator.permissions
      .query({ name: "notifications" as PermissionName })
      .then((status) => {
        const sync = () => setSharedPermission(readNativePermission());
        status.addEventListener("change", sync);
      })
      .catch(() => {
        // Some browsers reject `notifications` as a query name — ignore and
        // rely on the focus fallback.
      });
  }

  window.addEventListener("focus", () => {
    setSharedPermission(readNativePermission());
  });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): BrowserNotificationPermissionState {
  return sharedState;
}

// SSR snapshot — we can't know the real permission until hydration, so report
// the uninitialized state (permission-dependent UI stays off the page until
// the client reads the real value).
function getServerSnapshot(): BrowserNotificationPermissionState {
  return UNINITIALIZED_STATE;
}

// Module-level version of the hook's request() so non-hook callers (e.g. the
// system-toast action registry) can trigger the native permission prompt.
// State updates flow through the same setSharedPermission so any subscribed
// hooks pick up the new value.
export async function requestBrowserNotificationPermission(): Promise<BrowserNotificationPermission> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  const result = await window.Notification.requestPermission();
  setSharedPermission(result as BrowserNotificationPermission);
  return result as BrowserNotificationPermission;
}

// Test-only: reset the module-level singleton so each test starts cold. Not
// part of the public API — production code never needs to clear this state.
export function __resetBrowserNotificationPermissionForTests() {
  sharedState = UNINITIALIZED_STATE;
  sharedInitialized = false;
  listeners.clear();
}

export function useBrowserNotificationPermission() {
  const { permission, isInitialized } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  useEffect(() => {
    initShared();
  }, []);

  const request = useCallback(() => requestBrowserNotificationPermission(), []);

  return {
    permission,
    isInitialized,
    isSupported: permission !== "unsupported",
    request,
  };
}
